import React from 'react';
import { Link } from 'react-router-dom';
import { heroData, valueProps, industries } from '../mockData';
import { Zap, CheckCircle, DollarSign, MapPin, Shield, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const iconMap = {
  Zap: Zap,
  CheckCircle: CheckCircle,
  DollarSign: DollarSign,
  MapPin: MapPin,
  Shield: Shield
};

const Home = () => {
  return (
    <div style={{ backgroundColor: '#1a1c1b' }}>
      {/* Hero Section */}
      <section style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        padding: '120px 40px 80px',
        overflow: 'hidden'
      }}>
        {/* Background Image with Overlay */}
        <div style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0
        }}>
          <img
            src={heroData.heroImage}
            alt="Professional workspace"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.85), rgba(0,0,0,0.4))',
            opacity: 0.9
          }} />
        </div>

        {/* Hero Content */}
        <div style={{
          position: 'relative',
          zIndex: 1,
          maxWidth: '87.5rem',
          margin: '0 auto',
          width: '100%'
        }}>
          <div style={{ maxWidth: '50rem' }}>
            <h1 style={{
              fontFamily: 'Arial, sans-serif',
              fontWeight: 900,
              fontSize: 'clamp(3rem, 5vw, 6rem)',
              lineHeight: 0.95,
              color: '#d9fb06',
              textTransform: 'uppercase',
              marginBottom: '24px',
              letterSpacing: '-0.02em'
            }}>
              {heroData.tagline}
            </h1>
            <p style={{
              fontFamily: 'Inter, Arial, sans-serif',
              fontSize: 'clamp(1.1rem, 2vw, 1.5rem)',
              lineHeight: 1.5,
              color: '#dfddd6',
              marginBottom: '16px',
              maxWidth: '45rem'
            }}>
              {heroData.subheading}
            </p>
            <p style={{
              fontFamily: 'Inter, Arial, sans-serif',
              fontSize: 'clamp(1rem, 1.8vw, 1.25rem)',
              lineHeight: 1.4,
              color: '#888680',
              marginBottom: '40px',
              maxWidth: '45rem',
              fontWeight: 500
            }}>
              {heroData.strapline}
            </p>
            <div style={{
              display: 'flex',
              gap: '16px',
              flexWrap: 'wrap'
            }}>
              <Link to="/contact?form=workforce" style={{ textDecoration: 'none' }}>
                <Button
                  style={{
                    background: '#d9fb06',
                    color: '#1a1c1b',
                    border: 'none',
                    borderRadius: '10rem',
                    padding: '1.2em 2em',
                    fontFamily: 'Inter, Arial, sans-serif',
                    fontWeight: 600,
                    fontSize: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    textTransform: 'uppercase',
                    letterSpacing: '-0.02em'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.02)';
                    e.currentTarget.style.opacity = '0.9';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.opacity = '1';
                  }}
                >
                  Request Workforce
                </Button>
              </Link>
              <Link to="/for-suppliers" style={{ textDecoration: 'none' }}>
                <Button
                  style={{
                    background: 'transparent',
                    color: '#d9fb06',
                    border: '1px solid #d9fb06',
                    borderRadius: '10rem',
                    padding: '1.2em 2em',
                    fontFamily: 'Inter, Arial, sans-serif',
                    fontWeight: 600,
                    fontSize: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    textTransform: 'uppercase',
                    letterSpacing: '-0.02em'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#d9fb06';
                    e.currentTarget.style.color = '#1a1c1b';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#d9fb06';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  Become a Supplier
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition Strip */}
      <section style={{
        backgroundColor: '#302f2c',
        padding: '40px 20px',
        borderTop: '1px solid rgba(63, 72, 22, 0.5)',
        borderBottom: '1px solid rgba(63, 72, 22, 0.5)'
      }}>
        <div style={{
          maxWidth: '87.5rem',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '32px'
        }}>
          {valueProps.map((prop, index) => {
            const IconComponent = iconMap[prop.icon];
            return (
              <div key={index} style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                gap: '12px'
              }}>
                <IconComponent size={32} style={{ color: '#d9fb06' }} />
                <span style={{
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: '#d9fb06',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  {prop.title}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      {/* Intro Statement */}
      <section style={{
        padding: '96px 40px',
        backgroundColor: '#1a1c1b'
      }}>
        <div style={{
          maxWidth: '87.5rem',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontFamily: 'Arial, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2rem, 4vw, 3.5rem)',
            lineHeight: 1.1,
            color: '#d9fb06',
            marginBottom: '32px',
            textTransform: 'uppercase'
          }}>
            One partner. Every workforce you need.
          </h2>
          <p style={{
            fontFamily: 'Inter, Arial, sans-serif',
            fontSize: '1.25rem',
            lineHeight: 1.6,
            color: '#dfddd6',
            maxWidth: '60rem',
            margin: '0 auto 24px'
          }}>
            Worklink Global is a premium workforce aggregator connecting businesses with reliable labour, professional subcontractors, and specialised service providers across Australia. Through a multi-supplier network, we ensure fast mobilisation, strong compliance, and consistent delivery across construction, logistics, manufacturing, hospitality, cleaning, ICT, and more.
          </p>
          <p style={{
            fontFamily: 'Inter, Arial, sans-serif',
            fontSize: '1.1rem',
            lineHeight: 1.5,
            color: '#888680',
            maxWidth: '60rem',
            margin: '0 auto',
            fontWeight: 500,
            fontStyle: 'italic'
          }}>
            Workforce and supply coordination, simplified.
          </p>
        </div>
      </section>

      {/* Industries Grid */}
      <section style={{
        padding: '96px 40px',
        backgroundColor: '#302f2c'
      }}>
        <div style={{
          maxWidth: '87.5rem',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontFamily: 'Arial, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            lineHeight: 1.1,
            color: '#d9fb06',
            marginBottom: '48px',
            textTransform: 'uppercase'
          }}>
            Industries We Serve
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '32px'
          }}>
            {industries.map((industry) => (
              <div
                key={industry.id}
                style={{
                  backgroundColor: '#1a1c1b',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  transition: 'transform 0.3s ease',
                  cursor: 'pointer',
                  border: '1px solid rgba(63, 72, 22, 0.5)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <div style={{
                  height: '240px',
                  overflow: 'hidden'
                }}>
                  <img
                    src={industry.image}
                    alt={industry.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </div>
                <div style={{ padding: '24px' }}>
                  <h3 style={{
                    fontFamily: 'Arial, sans-serif',
                    fontWeight: 700,
                    fontSize: '1.25rem',
                    color: '#d9fb06',
                    marginBottom: '12px',
                    textTransform: 'uppercase'
                  }}>
                    {industry.name}
                  </h3>
                  <p style={{
                    fontFamily: 'Inter, Arial, sans-serif',
                    fontSize: '1rem',
                    color: '#888680',
                    lineHeight: 1.5
                  }}>
                    {industry.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section style={{
        padding: '96px 40px',
        backgroundColor: '#1a1c1b'
      }}>
        <div style={{
          maxWidth: '87.5rem',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontFamily: 'Arial, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            lineHeight: 1.1,
            color: '#d9fb06',
            marginBottom: '48px',
            textTransform: 'uppercase'
          }}>
            Why Choose Worklink Global?
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px',
            textAlign: 'left'
          }}>
            {[
              'Multi-industry supplier network',
              'Backup labour guarantees',
              'Strict compliance screening',
              'End-to-end workforce coordination',
              'Integrated materials & supply management',
              'Skilled teams and individual workers',
              'Project-based engagement to match market rates',
              'Premium service standards for Tier 1, Tier 2 & government clients'
            ].map((item, index) => (
              <div key={index} style={{
                padding: '24px',
                backgroundColor: '#302f2c',
                borderRadius: '8px',
                border: '1px solid rgba(63, 72, 22, 0.5)',
                transition: 'background-color 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3f4816'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#302f2c'}
              >
                <CheckCircle size={24} style={{ color: '#d9fb06', marginBottom: '12px' }} />
                <p style={{
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontSize: '1rem',
                  color: '#dfddd6',
                  lineHeight: 1.5,
                  margin: 0
                }}>
                  {item}
                </p>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '48px' }}>
            <Link to="/about" style={{ textDecoration: 'none' }}>
              <Button
                style={{
                  background: '#d9fb06',
                  color: '#1a1c1b',
                  border: 'none',
                  borderRadius: '10rem',
                  padding: '1.2em 2em',
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontWeight: 600,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textTransform: 'uppercase',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                Learn More About Us
                <ArrowRight size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '96px 40px',
        backgroundColor: '#3f4816',
        borderTop: '1px solid rgba(63, 72, 22, 0.5)'
      }}>
        <div style={{
          maxWidth: '60rem',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontFamily: 'Arial, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            lineHeight: 1.1,
            color: '#d9fb06',
            marginBottom: '24px',
            textTransform: 'uppercase'
          }}>
            Ready to Get Started?
          </h2>
          <p style={{
            fontFamily: 'Inter, Arial, sans-serif',
            fontSize: '1.25rem',
            lineHeight: 1.6,
            color: '#dfddd6',
            marginBottom: '40px'
          }}>
            Connect with Australia's trusted workforce aggregator today.
          </p>
          <div style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <Link to="/contact?form=workforce" style={{ textDecoration: 'none' }}>
              <Button
                style={{
                  background: '#d9fb06',
                  color: '#1a1c1b',
                  border: 'none',
                  borderRadius: '10rem',
                  padding: '1.2em 2em',
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontWeight: 600,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textTransform: 'uppercase'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                Request Workforce
              </Button>
            </Link>
            <Link to="/for-suppliers" style={{ textDecoration: 'none' }}>
              <Button
                style={{
                  background: 'transparent',
                  color: '#d9fb06',
                  border: '2px solid #d9fb06',
                  borderRadius: '10rem',
                  padding: '1.2em 2em',
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontWeight: 600,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textTransform: 'uppercase'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#d9fb06';
                  e.currentTarget.style.color = '#1a1c1b';
                  e.currentTarget.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#d9fb06';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                Become a Supplier
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
